import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import EnhancedSelect from '../../enhanced-select/jsx/EnhancedSelect';

import { getFacetOptionLabelByKey } from '../../_global/js/searchHelpers';

/**
 * Decision tree data for the input options
 * @const {array}
 */
const DECISION_TREE_DATA = [
    // List of options for first input, branching out from there
    {
        inputLabel: 'Early Childhood Educator',
        inputId: 'stageEarlyChildhood',
        nextStep: [
            {
                inputLabel: 'Lesson Plans',
                inputId: 'typeLessonPlan', // Input ID is the Funnelback Facet value
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all', // This means show all results for the selected audience and type. e.g. "Early Childhood Educator" AND "Lesson Plans"
                        nextStep: [], // Empty Array. Stop here and show search button
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Programs',
                inputId: 'typeProgram',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [], // Empty Array. Stop here and show search button
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Policies',
                inputId: 'typePolicy',
                nextStep: [], // Empty Array. Stop here and show search button
            },
            {
                inputLabel: 'Additional Resources',
                inputId: 'typeAdditionalResource',
                nextStep: [], // Empty Array. Stop here and show search button
            },
            {
                inputLabel: 'All Resources',
                inputId: 'all', // This means show all results for the selected audience option. e.g. "Early Childhood Educator"
                nextStep: [], // Empty Array. Stop here and show search button
            },
        ],
    },
    {
        inputLabel: 'Primary School Teacher',
        inputId: 'stagePrimarySchool',
        nextStep: [
            {
                inputLabel: 'Lesson Plans',
                inputId: 'typeLessonPlan',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Programs',
                inputId: 'typeProgram',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Policies',
                inputId: 'typePolicy',
                nextStep: [],
            },
            {
                inputLabel: 'Additional Resources',
                inputId: 'typeAdditionalResource',
                nextStep: [],
            },
            {
                inputLabel: 'All Resources',
                inputId: 'all',
                nextStep: [],
            },
        ],
    },
    {
        inputLabel: 'Secondary School Teacher',
        inputId: 'stageSecondarySchool',
        nextStep: [
            {
                inputLabel: 'Lesson Plans',
                inputId: 'typeLessonPlan',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Programs',
                inputId: 'typeProgram',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Policies',
                inputId: 'typePolicy',
                nextStep: [],
            },
            {
                inputLabel: 'Additional Resources',
                inputId: 'typeAdditionalResource',
                nextStep: [],
            },
            {
                inputLabel: 'All Resources',
                inputId: 'all',
                nextStep: [],
            },
        ],
    },
    {
        inputLabel: 'School Leader',
        inputId: 'optionSchoolLeader', // There is no corresponding facet, but we still need an ID
        nextStep: [
            {
                inputLabel: 'Early Childhood Centre',
                inputId: 'stageEarlyChildhood',
                nextStep: [
                    {
                        inputLabel: 'All Resources',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Primary School',
                inputId: 'stagePrimarySchool',
                nextStep: [
                    {
                        inputLabel: 'All Resources',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Secondary School',
                inputId: 'stageSecondarySchool',
                nextStep: [
                    {
                        inputLabel: 'All Resources',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
        ],
    },
    {
        inputLabel: 'Year Level Coordinator',
        inputId: 'optionYearLevelCoordinator',
        nextStep: [
            {
                inputLabel: 'Lesson Plans',
                inputId: 'typeLessonPlan',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Programs',
                inputId: 'typeProgram',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Policies',
                inputId: 'typePolicy',
                nextStep: [],
            },
            {
                inputLabel: 'Additional Resources',
                inputId: 'typeAdditionalResource',
                nextStep: [],
            },
            {
                inputLabel: 'All Resources',
                inputId: 'all',
                nextStep: [
                    {
                        inputLabel: 'Early Childhood Centre',
                        inputId: 'stageEarlyChildhood',
                        nextStep: [],
                    },
                    {
                        inputLabel: 'Primary School',
                        inputId: 'stagePrimarySchool',
                        nextStep: [],
                    },
                    {
                        inputLabel: 'Secondary School',
                        inputId: 'stageSecondarySchool',
                        nextStep: [],
                    },
                ],
            },
        ],
    },
    {
        inputLabel: 'Subject Coordinator',
        inputId: 'optionSubjectCoordinator',
        nextStep: [
            {
                inputLabel: 'Lesson Plans',
                inputId: 'typeLessonPlan',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Programs',
                inputId: 'typeProgram',
                nextStep: [
                    {
                        inputLabel: 'Show All',
                        inputId: 'all',
                        nextStep: [],
                    }, // Plus do a FB lookup here for any other available options
                ],
            },
            {
                inputLabel: 'Policies',
                inputId: 'typePolicy',
                nextStep: [],
            },
            {
                inputLabel: 'Additional Resources',
                inputId: 'typeAdditionalResource',
                nextStep: [],
            },
            {
                inputLabel: 'All Resources',
                inputId: 'all',
                nextStep: [
                    {
                        inputLabel: 'Early Childhood Centre',
                        inputId: 'stageEarlyChildhood',
                        nextStep: [],
                    },
                    {
                        inputLabel: 'Primary School',
                        inputId: 'stagePrimarySchool',
                        nextStep: [],
                    },
                    {
                        inputLabel: 'Secondary School',
                        inputId: 'stageSecondarySchool',
                        nextStep: [],
                    },
                ],
            },
        ],
    },
    {
        inputLabel: 'Learner/Novice Driver',
        inputId: 'stageLearnerNoviceDriver',
        nextStep: [
            {
                inputLabel: 'Lesson Plans',
                inputId: 'typeLessonPlan',
                nextStep: [], // Stop here, no input 3
            },
            {
                inputLabel: 'Programs',
                inputId: 'typeProgram',
                nextStep: [],
            },
            {
                inputLabel: 'Additional Resources',
                inputId: 'typeAdditionalResource',
                nextStep: [],
            },
            {
                inputLabel: 'All Resources',
                inputId: 'all',
                nextStep: [],
            },
        ],
    },
];

/**
 * Mapping of value prefix to a Funnelback Facet name
 * that can be used for search filtering
 * @const {object}
 */
const FUNNELBACK_FACET_MAP = {
    type: 'f.Resource+Type%7CresourceType',
    curriculum: 'f.Curriculum+Area%7CcurriculumArea',
    stage: 'f.Stage%7Cstage',
    grade: 'f.Year+Level%7CgradeLevel',
    learningMode: 'f.Learning+Mode%7ClearningMode',
};

/**
 * Controller for cancelling pending XHR requests.
 *
 * @source https://www.npmjs.com/package/axios#cancellation
 */
let funnelbackRequestController = null;

/**
 * Reducer to update the userInputs in the local state
 *
 * @param {Object} state The current state of the userInputs
 * @param {Object} action The "type" and "payload" to apply to the userInputs
 *
 * @returns Object The new userInput data
 */
function updateUserInputs(state, action) {
    let updatedState = null;
    let inputId = null;
    let inputValue = null;

    switch (action.type) {
        case 'reset':
            // Replace the whole user state with the provided data
            updatedState = action.payload;

            return updatedState;

        case 'update_value':
            /**
             * Expects an Array of two values:
             * [0] The Object Key/ID
             * [1] The new value
             */
            inputId = action.payload[0];
            inputValue = action.payload[1];

            // Replace the existing value
            updatedState = {
                ...state,
                [inputId]: inputValue,
            };

            /**
             * If the user has made a change to input 1 or 2,
             * then reset the proceeding inputs.
             */
            if (inputId === 'nlfStageOne') {
                updatedState.nlfStageTwo = '';
                updatedState.nlfStageThree = '';
            }

            if (inputId === 'nlfStageTwo') {
                updatedState.nlfStageThree = '';
            }

            // Logging for testing
            // console.log('UPDATE CONNECTING PHRASES:');
            // console.table({
            //     'First input response >>': updatedState.nlfStageOne,
            //     'Second input response >>': updatedState.nlfStageTwo,
            //     'Third input response >>': updatedState.nlfStageThree,
            // });

            /**
             * Set the connecting phrases based on the selected
             * options for nlfStageOne, nlfStageTwo and
             * nlfStageThree.
             */

            // Response to the first input
            switch (updatedState.nlfStageOne) {
                case 'optionSchoolLeader':
                    updatedState.nlfStageTwoConnectingPhraseBefore = 'of a';
                    break;

                default:
                    updatedState.nlfStageTwoConnectingPhraseBefore =
                        'and I am looking for';
                    break;
            }

            // Response to the second input
            switch (true) {
                case updatedState.nlfStageOne === 'stageLearnerNoviceDriver' &&
                    updatedState.nlfStageTwo === 'typeAdditionalResource':
                case updatedState.nlfStageOne === 'stageLearnerNoviceDriver' &&
                    updatedState.nlfStageTwo === 'all':
                case updatedState.nlfStageOne === 'optionYearLevelCoordinator' &&
                    updatedState.nlfStageTwo === 'all':
                case updatedState.nlfStageOne === 'optionSubjectCoordinator' &&
                    updatedState.nlfStageTwo === 'all':
                    updatedState.nlfFormSummary = '';
                    break;

                case updatedState.nlfStageTwo === 'typeAdditionalResource':
                case updatedState.nlfStageTwo === 'typePolicy':
                case updatedState.nlfStageTwo === 'all':
                    updatedState.nlfFormSummary =
                        'to teach road safety education.';
                    break;

                case updatedState.nlfStageTwo === 'stageEarlyChildhood':
                case updatedState.nlfStageTwo === 'stagePrimarySchool':
                case updatedState.nlfStageTwo === 'stageSecondarySchool':
                    updatedState.nlfStageThreeConnectingPhraseBefore =
                        'looking for';
                    updatedState.nlfStageThreeConnectingPhraseAfter =
                        'to teach road safety.';
                    break;

                default:
                    updatedState.nlfStageThreeConnectingPhraseBefore =
                        'to teach my';
                    updatedState.nlfStageThreeConnectingPhraseAfter =
                        'class about road safety.';
                    updatedState.nlfFormSummary = '';
                    break;
            }

            // Response to the third input
            switch (updatedState.nlfStageThree) {
                case 'all':
                    updatedState.nlfStageThreeConnectingPhraseBefore = '';
                    updatedState.nlfStageThreeConnectingPhraseAfter = '';
                    break;

                default:
                    break;
            }

            return updatedState;

        case 'update_input':
            /**
             * Expects an Array of two values:
             * [0] The Object Key/ID
             * [1] The new value
             */
            updatedState = {
                ...state,
                [action.payload[0]]: action.payload[1],
            };

            return updatedState;

        default:
            console.log(
                'Error in NaturalLanguageForm.jsx. Unable to update user state data.'
            );
            return {
                ...state,
            };
    }
}

export default function NaturalLanguageForm({ searchUrl, lookupUrl }) {
    const searchPageURL = searchUrl;
    const [showSubmit, setShowSubmit] = useState(false);
    const [numberOfResources, setNumberOfResources] = useState('');
    const [isRequestingStageThreeOptions, setIsRequestingStageThreeOptions] =
        useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Used to track the values selected for each input
    const [inputValues, dispatchInputUpdate] = useReducer(updateUserInputs, {
        nlfStageOne: '',
        nlfStageTwo: '',
        nlfStageThree: '',
        nlfStageOneConnectingPhraseBefore: "I'm a",
        nlfStageTwoConnectingPhraseBefore: '',
        nlfStageThreeConnectingPhraseBefore: '',
        nlfStageThreeConnectingPhraseAfter: '',
        nlfFormSummary: '',
    });

    // Used to track the question list for each input
    const [inputOptions, dispatchInputOptions] = useReducer(updateUserInputs, {
        nlfStageOne: '',
        nlfStageTwo: '',
        nlfStageThree: '',
    });

    /**
     * Operations to perform when the component first mounts.
     */
    useEffect(() => {
        // Store the first question options
        // Add the first "select" option
        const parsedOptions = [
            {
                label: 'Select an option',
                value: '',
            },
        ];

        // Convert the data into the format for the Enhanced Select component
        parsedOptions.push(
            ...DECISION_TREE_DATA.map((inputOption) => {
                return {
                    label: inputOption.inputLabel,
                    value: inputOption.inputId,
                };
            })
        );

        dispatchInputOptions({
            type: 'update_input',
            payload: ['nlfStageOne', parsedOptions],
        });
    }, []);

    /**
     * Respond to selections in the EnhancedSelect components
     *
     * @param {Object} selectionData Data for the selected option. e.g. label, value and ID of the element
     *
     * @returns void
     */
    const onSelectionChange = (selectionData) => {
        const inputValue = selectionData.value;
        // Extract the input ID from the element ID
        const inputId = selectionData.elementId.split('-')[0];
        let nextQuestionData = [];
        let nextStageId = null;
        let parsedOptions = '';

        // Cancel any pending Funnelback request for Stage Three options
        if (funnelbackRequestController !== null) {
            funnelbackRequestController.abort();
        }

        if (inputId === '') {
            return;
        }

        if (inputValue !== '') {
            // Hide error message (if any currently set)
            setErrorMessage(null);
        }

        switch (inputId) {
            case 'nlfStageOne':
                nextStageId = 'nlfStageTwo';
                // eslint-disable-next-line no-use-before-define
                nextQuestionData = lookupNextStepData(inputValue);
                break;

            case 'nlfStageTwo':
                nextStageId = 'nlfStageThree';
                // eslint-disable-next-line no-use-before-define
                nextQuestionData = lookupNextStepData(
                    inputValue,
                    inputValues.nlfStageOne
                );
                break;

            case 'nlfStageThree':
                // eslint-disable-next-line no-use-before-define
                nextQuestionData = lookupNextStepData(
                    inputValue,
                    inputValues.nlfStageOne
                );
                break;

            default:
                break;
        }

        if (nextQuestionData.length !== 0) {
            // Convert the data into the format for the Enhanced Select component
            parsedOptions = [
                {
                    label: 'Select an option',
                    value: '',
                },
            ];

            parsedOptions.push(
                ...nextQuestionData.map((inputOption) => {
                    return {
                        label: inputOption.inputLabel,
                        value: inputOption.inputId,
                    };
                })
            );
        }

        // Update the local state with the input value
        dispatchInputUpdate({
            type: 'update_value',
            payload: [inputId, inputValue],
        });

        // Update the local state with the next input options
        if (nextStageId !== null) {
            dispatchInputOptions({
                type: 'update_input',
                payload: [nextStageId, parsedOptions],
            });
        }

        // Determine what to show to the user next

        if (inputId === 'nlfStageOne') {
            // User has only completed Stage One
            // Hide the submit/search button
            setShowSubmit(false);
        }

        if (
            inputId === 'nlfStageTwo' &&
            inputValue !== '' &&
            nextQuestionData.length === 0
        ) {
            // User has answered Stage Two and there is no Stage Three

            // Reset the # of results count
            setNumberOfResources('');

            // Show the submit/search button
            setShowSubmit(true);

            // eslint-disable-next-line no-use-before-define
            retrieveNumberOfResults(inputValues.nlfStageOne, inputValue);
        }

        if (inputId === 'nlfStageTwo' && nextQuestionData.length !== 0) {
            // User has answered Stage Two and there are Stage Three questions
            // Hide the submit/search button
            setShowSubmit(false);

            /**
             * Check if multiple options are in the next question.
             * If they are, we assume we only want those options.
             * Otherwise, query Funnelback to get the next options to display
             */
            if (nextQuestionData.length === 1) {
                // Show the loading message
                setIsRequestingStageThreeOptions(true);

                // eslint-disable-next-line no-use-before-define
                retrieveStageThreeOptions(inputValues.nlfStageOne, inputValue);
            }
        }

        if (inputId === 'nlfStageThree' && inputValue !== '') {
            // User has answered Stage Three

            // Reset the # of results count
            setNumberOfResources('');

            // Show the submit/search button
            setShowSubmit(true);

            // eslint-disable-next-line no-use-before-define
            retrieveNumberOfResults(
                inputValues.nlfStageOne,
                inputValues.nlfStageTwo,
                inputValue
            );
        }
    };

    /**
     * Lookup and return a Funnelback search URL query string
     * (key/value pair) based on the prefix of a provided value
     *
     * @param {String} facetValue The value to use in the search. e.g. stageEarlyChildhood
     *
     * @returns String
     */
    function getFacetQueryByPrefix(facetValue) {
        let facetQuery = '';

        switch (true) {
            case facetValue.indexOf('type') === 0:
                facetQuery = `${FUNNELBACK_FACET_MAP.type}=${facetValue}`;
                break;

            case facetValue.indexOf('curriculum') === 0:
                facetQuery = `${FUNNELBACK_FACET_MAP.curriculum}=${facetValue}`;
                break;

            case facetValue.indexOf('stage') === 0:
                facetQuery = `${FUNNELBACK_FACET_MAP.stage}=${facetValue}`;
                break;

            case facetValue.indexOf('grade') === 0:
                facetQuery = `${FUNNELBACK_FACET_MAP.grade}=${facetValue}`;
                break;

            case facetValue.indexOf('learningMode') === 0:
                facetQuery = `${FUNNELBACK_FACET_MAP.learningMode}=${facetValue}`;
                break;

            default:
                break;
        }

        return facetQuery;
    }

    /**
     * Lookup and return a Funnelback search query parameter name
     * based on the prefix of a provided value.
     * e.g. "stageEarlyChildhood" would return; "f.Stage|stage"
     *
     * @param {String} facetValue The value to use in the search. e.g. stageEarlyChildhood
     *
     * @returns String
     */
    function getFacetQueryParamNameByPrefix(facetValue) {
        let facetQueryParamName = '';

        switch (true) {
            case facetValue.indexOf('type') === 0:
                facetQueryParamName = 'f.Resource Type|resourceType';
                break;

            case facetValue.indexOf('curriculum') === 0:
                facetQueryParamName = 'f.Curriculum Area|curriculumArea';
                break;

            case facetValue.indexOf('stage') === 0:
                facetQueryParamName = 'f.Stage|stage';
                break;

            case facetValue.indexOf('grade') === 0:
                facetQueryParamName = 'f.Year Level|gradeLevel';
                break;

            case facetValue.indexOf('learningMode') === 0:
                facetQueryParamName = 'f.Learning Mode|learningMode';
                break;

            default:
                break;
        }

        return facetQueryParamName;
    }

    /**
     * Make a request to Funnelback (via Matrix proxy) to get
     * the number of results that would be returned for the
     * currently selected filters.
     *
     * Note: A value of "all" is ignored
     *
     * @param {String} stageOneValue The value to use for the first Facet
     * @param {String} stageTwoValue The value to use for the second Facet
     * @param {String} stageThreeValue The value to use for the third Facet (optional)
     *
     * @returns void
     */
    function retrieveNumberOfResults(
        stageOneValue,
        stageTwoValue,
        stageThreeValue = ''
    ) {
        const searchFacets = [];
        const stageOneQuery = getFacetQueryByPrefix(stageOneValue);
        const stageTwoQuery = getFacetQueryByPrefix(stageTwoValue);
        const stageThreeQuery = getFacetQueryByPrefix(stageThreeValue);

        // Lookup the correct facets to use, based on the value prefixes
        if (stageOneQuery !== '') {
            searchFacets.push(stageOneQuery);
        }

        if (stageTwoQuery !== '') {
            searchFacets.push(stageTwoQuery);
        }

        if (stageThreeQuery !== '') {
            searchFacets.push(stageThreeQuery);
        }

        axios
            .get(`${lookupUrl}&${searchFacets.join('&')}`)
            .then((response) => {
                setNumberOfResources(
                    parseInt(
                        response.data.response.resultPacket.resultsSummary
                            .fullyMatching,
                        10
                    )
                );
            })
            .catch(() => {
                // Do nothing.
                // We only show the # of resources as a convenience
            });
    }

    /**
     * Make a request to Funnelback (via Matrix proxy) to get
     * the available Facets/filter options that would be returned for the
     * currently selected filters.
     *
     * Note: A value of "all" is ignored
     *
     * @param {String} stageOneValue The value to use for the first Facet
     * @param {String} stageTwoValue The value to use for the second Facet
     *
     * @returns void
     */
    function retrieveStageThreeOptions(stageOneValue, stageTwoValue) {
        const searchFacets = [];
        const stageOneQuery = getFacetQueryByPrefix(stageOneValue);
        const stageTwoQuery = getFacetQueryByPrefix(stageTwoValue);
        // const stageOneFacet = getFacetQueryParamNameByPrefix(stageOneValue);
        const stageTwoFacet = getFacetQueryParamNameByPrefix(stageTwoValue);

        // Lookup the correct facets to use, based on the value prefixes
        if (stageOneQuery !== '') {
            searchFacets.push(stageOneQuery);
        }

        if (stageTwoQuery !== '') {
            searchFacets.push(stageTwoQuery);
        }

        // Create a new controller instance
        funnelbackRequestController = new AbortController();

        axios
            .get(`${lookupUrl}&${searchFacets.join('&')}`, {
                signal: funnelbackRequestController.signal,
            })
            .then((response) => {
                const responseFacetsArray = response.data.response.facets;
                const parsedOptions = [
                    {
                        label: 'Select an option',
                        value: '',
                    },
                ];
                const facetOptionsUnsorted = [];
                const facetOptionsSorted = [];
                const facetOptionsSortedAdditional = [];
                let funnelbackFacetId = '';

                // Clear the request controller
                funnelbackRequestController = null;

                /**
                 * Get the Stage Three options based on the Facets
                 * selected in Stage One and Stage Two.
                 */
                switch (true) {
                    case stageOneValue === 'stageEarlyChildhood':
                    case stageOneValue === 'stagePrimarySchool':
                    case stageOneValue === 'stageSecondarySchool':
                        /**
                         * User selected: Either Early Childhood Educator, Primary School Teacher
                         * or Secondary School Teacher and either Lesson Plans or Programs
                         * Get the options for: Curriculum Area
                         */
                        funnelbackFacetId = 'Curriculum Area';
                        break;

                    case stageOneValue === 'optionYearLevelCoordinator':
                        /**
                         * User selected: Year Level Coordinator and either Programs or Policies
                         * Get the options for: Grade Level
                         */
                        funnelbackFacetId = 'Year Level';
                        break;

                    case stageTwoFacet === 'f.Resource Type|resourceType':
                        /**
                         * User selected: Either Programs or Policies
                         * (regardless of Stage One selection)
                         * Get the options for: Curriculum Area
                         */
                        funnelbackFacetId = 'Curriculum Area';
                        break;

                    case stageTwoFacet === 'f.Stage|stage':
                        /**
                         * User selected: Either Early Childhood, Primary School or Secondary School
                         * (regardless of Stage One selection)
                         * Get the options for: Resource Type
                         */
                        funnelbackFacetId = 'Stage';
                        break;

                    default:
                        // Maybe show an error here?
                        // e.g. Same as when there is no FB response
                        break;
                }

                // Retrieve the options from the Funnelback response
                if (funnelbackFacetId !== '') {
                    for (
                        let i = 0, j = responseFacetsArray.length;
                        i < j;
                        ++i
                    ) {
                        if (
                            responseFacetsArray[i].allValues.length !== 0 &&
                            responseFacetsArray[i].name === funnelbackFacetId
                        ) {
                            // Found some matching option(s) for the user to select
                            // Capture the available option(s)
                            facetOptionsUnsorted.push(
                                ...responseFacetsArray[i].allValues.map(
                                    (inputOption) => {
                                        return {
                                            label: getFacetOptionLabelByKey(
                                                inputOption.label,
                                                true
                                            ),
                                            value: inputOption.label,
                                        };
                                    }
                                )
                            );
                        }
                    }
                }

                // Sort the Funnelback options (specifically for the year values)
                for (let i = 0, j = facetOptionsUnsorted.length; i < j; ++i) {
                    if (
                        facetOptionsUnsorted[i].label.indexOf('Year 10') !== -1 ||
                        facetOptionsUnsorted[i].label.indexOf('Year 11') !== -1 ||
                        facetOptionsUnsorted[i].label.indexOf('Year 12') !== -1
                    ) {
                        // If  value is: Year 10, 11 or 12, move those to the end
                        facetOptionsSortedAdditional.push(
                            facetOptionsUnsorted[i]
                        );
                    } else {
                        // Capture the values "as is"
                        facetOptionsSorted.push(facetOptionsUnsorted[i]);
                    }
                }

                // Add the Funnelback options to the parsed options
                parsedOptions.push(
                    ...facetOptionsSorted,
                    ...facetOptionsSortedAdditional
                );

                // Add the "show all" option
                parsedOptions.push({
                    label: 'Show All',
                    value: 'all',
                });

                // Update the Stage Three dropdown to include these additional options
                dispatchInputOptions({
                    type: 'update_input',
                    payload: ['nlfStageThree', parsedOptions],
                });

                // Hide the loading message
                setIsRequestingStageThreeOptions(false);
            })
            .catch((error) => {
                if (error.code === 'ERR_CANCELED') {
                    // The request was cancelled, do nothing.
                    return;
                }

                // Hide the loading message
                setIsRequestingStageThreeOptions(false);

                // Show an error message
                setErrorMessage(
                    'Warning : Unable to find any results. Please try another option.'
                );
            });
    }

    /**
     * Iterate over the DECISION_TREE_DATA data to find the options
     * for the next input
     *
     * @param {String} inputId The input ID to match on when looking up the question data
     * @param {String} previousInputId Optional. Supply the previous input ID when looking up stage 2 data (for stage 3 questions)
     *
     * @usage
     *
     * @returns Array
     */
    function lookupNextStepData(inputId, previousInputId = null) {
        let nextStepData = [];

        // Iterating through the top-level decision tree data
        for (let i = 0, j = DECISION_TREE_DATA.length; i < j; ++i) {
            if (previousInputId !== null) {
                // Look up Step 3 data, by first finding the correct parent in the tree data
                if (DECISION_TREE_DATA[i].inputId === previousInputId) {
                    // Look for nested Step 3 data
                    for (
                        let b = 0, x = DECISION_TREE_DATA[i].nextStep.length;
                        b < x;
                        ++b
                    ) {
                        if (
                            DECISION_TREE_DATA[i].nextStep[b].inputId ===
                            inputId
                        ) {
                            nextStepData =
                                DECISION_TREE_DATA[i].nextStep[b].nextStep;
                            break;
                        }
                    }
                    break;
                }
            } else if (DECISION_TREE_DATA[i].inputId === inputId) {
                // Look for Step 2 data
                nextStepData = DECISION_TREE_DATA[i].nextStep;
                break;
            }
        }

        return nextStepData;
    }

    /**
     * Create hidden inputs that can be inserted into the form.
     *
     * These will be included as part of the form submission
     * and will be the query paramaters/facets submitted
     * to Funnelback.
     *
     * @param {String} selectedValue The value to use for the input
     *
     * @returns JSX
     */
    function generateHiddenInput(selectedValue) {
        // Lookup the corresponding facet parameter for the value
        const facetQueryName = getFacetQueryParamNameByPrefix(selectedValue);

        return facetQueryName !== '' ? (
            <input type="hidden" name={facetQueryName} value={selectedValue} />
        ) : null;
    }

    return (
        <form
            action={searchPageURL}
            method="get"
            className={`nlf-component-form__form ${
                inputValues.nlfStageOne !== '' ? 'is-active' : null
            }`}
        >
            <div className="nlf-component-form__row">
                {generateHiddenInput(inputValues.nlfStageOne)}
                <p className="nlf-component-form__descriptor-label">
                    {inputValues.nlfStageOneConnectingPhraseBefore}
                </p>
                {inputOptions.nlfStageOne !== '' ? (
                    <EnhancedSelect
                        componentId="nlfStageOne"
                        initialOption={{
                            value: inputValues.nlfStageOne,
                        }}
                        selectionCallback={onSelectionChange}
                        selectOptions={inputOptions.nlfStageOne}
                        allowEmptySelection={false}
                    />
                ) : null}
            </div>

            {inputValues.nlfStageOne !== '' ? (
                <div className="nlf-component-form__row">
                    {generateHiddenInput(inputValues.nlfStageTwo)}
                    {inputOptions.nlfStageTwo !== '' ? (
                        <>
                            <p className="nlf-component-form__descriptor-label">
                                {inputValues.nlfStageTwoConnectingPhraseBefore}
                            </p>
                            <EnhancedSelect
                                componentId="nlfStageTwo"
                                initialOption={{
                                    value: inputValues.nlfStageTwo,
                                }}
                                selectionCallback={onSelectionChange}
                                selectOptions={inputOptions.nlfStageTwo}
                                allowEmptySelection={false}
                            />
                        </>
                    ) : null}
                    {inputValues.nlfStageTwo !== '' &&
                    inputOptions.nlfStageThree !== '' ? (
                        <p className="nlf-component-form__descriptor-label">
                            {inputValues.nlfStageThreeConnectingPhraseBefore}
                        </p>
                    ) : null}
                </div>
            ) : null}

            {inputValues.nlfStageTwo !== '' &&
            inputOptions.nlfStageThree !== '' ? (
                <div className="nlf-component-form__row">
                    {generateHiddenInput(inputValues.nlfStageThree)}
                    {isRequestingStageThreeOptions ? (
                        <div className="nlf-component-form__loading-wrapper">
                            <p className="nlf-component-form__loading-message">
                                <em>Searching for options</em>
                            </p>
                            <div className="nlf-component-form__loading-animation" />
                        </div>
                    ) : (
                        <>
                            <EnhancedSelect
                                componentId="nlfStageThree"
                                initialOption={{
                                    value: inputValues.nlfStageThree,
                                }}
                                selectionCallback={onSelectionChange}
                                selectOptions={inputOptions.nlfStageThree}
                                allowEmptySelection={false}
                            />
                            <p className="nlf-component-form__descriptor-label">
                                {inputValues.nlfStageThreeConnectingPhraseAfter}
                            </p>
                        </>
                    )}
                </div>
            ) : null}

            {inputValues.nlfFormSummary !== '' ? (
                <div className="nlf-component-form__row">
                    <p className="nlf-component-form__descriptor-label">
                        {inputValues.nlfFormSummary}
                    </p>
                </div>
            ) : null}

            {errorMessage ? (
                <p className="nlf-component-form__error-message">
                    {errorMessage}
                </p>
            ) : null}

            {showSubmit ? (
                <button
                    type="submit"
                    className="nlf-component-form__submit-button"
                >
                    Show me {numberOfResources} resources
                    <svg className="svg-icon">
                        <use xlinkHref="#rse-chevron-right-16" fill="none" />
                    </svg>
                </button>
            ) : null}
        </form>
    );
}

NaturalLanguageForm.propTypes = {
    searchUrl: PropTypes.string,
    lookupUrl: PropTypes.string,
};

NaturalLanguageForm.defaultProps = {
    searchUrl: '',
    lookupUrl: '',
};
